<script setup lang="ts">
import { useId } from 'vue';

import { useConfirm } from '@/shared/lib/confirm/use-confirm';
import { discardChangesConfirm, useUnsavedChangesGuard } from '@/shared/lib/unsaved-changes/use-unsaved-changes-guard';
import { AppButton } from '@/shared/ui/button';
import { OverlayDialog } from '@/shared/ui/overlay';

/**
 * Редактор на весь экран поверх страницы: фон закрыт, доступна только открытая форма.
 * У редактора свой адрес (`useFullScreenModalRoute`), поэтому закрытие — уход с маршрута:
 * о несохранённых изменениях спрашивает guard и по «Закрыть», и по «Назад» браузера.
 */
const props = withDefaults(
  defineProps<{
    title: string;
    /** Пояснение под заголовком. */
    description?: string;
    dirty: boolean;
    /** Идёт сохранение: кнопки заблокированы, уйти нельзя. */
    saving?: boolean;
    /** Сохранять нечего: «Сохранить» неактивна. */
    saveDisabled?: boolean;
  }>(),
  { description: undefined, saving: false, saveDisabled: false },
);

const emit = defineEmits<{
  close: [];
  save: [];
}>();

const ENTER_TRANSITION = {
  enterActiveClass: 'transition duration-200 ease-out',
  enterFromClass: 'translate-y-2 opacity-0',
} as const;

const titleId = useId();
const { confirm } = useConfirm();

useUnsavedChangesGuard({
  hasUnsavedChanges: () => props.dirty || props.saving,
  confirmLeave: () => (props.saving ? false : confirm(discardChangesConfirm())),
});
</script>

<template>
  <OverlayDialog
    open
    :close-on-escape="false"
    :transition="ENTER_TRANSITION"
    :aria-labelledby="titleId"
    @request-close="emit('close')"
  >
    <div class="fixed inset-0 overflow-y-auto overscroll-contain bg-(--surface-page) text-(--text-primary)">
      <div
        class="sticky top-0 z-10 flex justify-end gap-2 border-b border-(--border-soft) bg-(--surface-page)/92 p-4 backdrop-blur-xl md:px-8 md:py-6"
      >
        <AppButton
          class="min-h-12"
          size="lg"
          outline
          :disabled="saving"
          @click="emit('close')"
        >
          Закрыть
        </AppButton>
        <AppButton
          class="min-h-12"
          size="lg"
          :disabled="saveDisabled"
          :loading="saving"
          @click="emit('save')"
        >
          Сохранить
        </AppButton>
      </div>

      <div class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-3">
            <h3
              :id="titleId"
              class="text-2xl leading-none font-semibold sm:text-3xl"
            >
              {{ title }}
            </h3>
            <!-- Строчный бокс заголовка выше самих букв: бейдж поднимаем к оптическому центру. -->
            <span
              v-if="$slots['title-badge']"
              class="-translate-y-0.5"
            >
              <slot name="title-badge" />
            </span>
          </div>
          <p
            v-if="description"
            class="text-sm text-(--text-secondary)"
          >
            {{ description }}
          </p>
        </div>
        <slot />
      </div>
    </div>
  </OverlayDialog>
</template>
